import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { AlertTriangle, ShieldAlert, X, Clock, CheckCircle2, CircleDot } from 'lucide-react';
import api from '../lib/api';
import { formatDateTime } from '../lib/constants';
import QuotaBanner from '../components/QuotaBanner';
import UpgradeRequestModal from '../components/UpgradeRequestModal';
import { useQuotaUsage } from '../lib/useQuotaUsage';
import MissionShell from '../components/MissionShell';
import { MISSIONS } from '../lib/missions';

const RISK_TONE = {
  bajo:    { color: '#065F46', bg: '#ECFDF5', label: 'Bajo' },
  medio:   { color: '#B45309', bg: '#FFFBEB', label: 'Medio' },
  alto:    { color: '#B91C1C', bg: '#FEF2F2', label: 'Alto' },
  critico: { color: '#7F1D1D', bg: '#FEE2E2', label: 'Crítico' },
};

const DATA_CAT_LABELS = {
  identificacion: 'Identificación', contacto: 'Contacto', financiera: 'Financiera',
  salud: 'Salud', biometrica: 'Biométrica', laboral: 'Laboral',
  geolocalizacion: 'Ubicación', sensible: 'Sensible', otra: 'Otra',
};

export default function Brechas() {
  const [meta, setMeta] = useState(null);
  const [items, setItems] = useState([]);
  const [systems, setSystems] = useState([]);
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const { usage, refresh: refreshUsage } = useQuotaUsage();
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [m, list, s] = await Promise.all([
        api.get('/breaches/meta'), api.get('/breaches'), api.get('/systems'),
      ]);
      setMeta(m.data); setItems(list.data); setSystems(s.data);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="text-sm text-[#6B7280]">Cargando…</div>;

  const active = items.filter((b) => !['cerrada', 'descartada'].includes(b.status));
  const notifiedCount = items.filter((b) => b.notification_agencia_sent_at).length;

  return (
    <MissionShell
      mission={MISSIONS.brechas}
      idPrefix="brechas-mission"
      prereq={systems.length === 0 ? { message: 'Registra tus sistemas y proveedores: ante una emergencia necesitarás indicar rápidamente qué sistemas fueron afectados.', to: '/app/sistemas', cta: 'Ir a Sistemas y proveedores' } : null}
      doHint={active.length > 0
        ? `Tienes ${active.length} brecha${active.length === 1 ? '' : 's'} activa${active.length === 1 ? '' : 's'}: entra a cada una y completa sus tareas legales antes del plazo.`
        : 'Sin incidentes activos. Revisa este flujo con tu equipo para saber qué hacer cuando ocurra uno; el botón rojo inicia el conteo de 72h.'}
      evidence={[
        { label: 'Brechas documentadas', value: items.length, tone: 'ok', detail: 'Etapas, tareas y fechas de cada incidente' },
        { label: 'Notificadas a la Agencia', value: notifiedCount, tone: 'ok' },
        { label: 'Activas ahora', value: active.length, tone: active.length ? 'bad' : 'ok' },
      ]}
      evidenceNote="Cada brecha guarda su cronología completa: es la evidencia que demuestra que actuaste dentro del plazo legal."
    >
    <div className="space-y-6" data-testid="brechas-page">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#E5E7EB] pb-5">
        <div>
          <div className="text-[15px] font-medium text-[#1C2329]">
            {items.length} brecha{items.length === 1 ? '' : 's'} · {active.length} activa{active.length === 1 ? '' : 's'}
          </div>
          <div className="text-[13px] text-[#6B7280]">
            Cada brecha abre un flujo de 6 etapas con tareas obligatorias por ley y otras sugeridas según el tipo.
          </div>
        </div>
        <button
          className="flex items-center gap-2 px-4 py-2 bg-[#DC2626] hover:bg-[#B91C1C] text-white font-medium text-[13px] shadow-sm"
          onClick={() => setCreating(true)} data-testid="brecha-new">
          <AlertTriangle size={14} /> Reportar brecha
        </button>
      </div>

      <QuotaBanner usage={usage} quotaKey="breaches_year" onRequestUpgrade={() => setShowUpgrade(true)} />
      <UpgradeRequestModal open={showUpgrade} onClose={(sent) => { setShowUpgrade(false); if (sent) refreshUsage(); }} />

      {items.length === 0 ? (
        <div className="border border-[#E5E7EB] bg-white p-10 text-center">
          <ShieldAlert size={26} className="mx-auto text-[#9CA3AF]" strokeWidth={1.5} />
          <div className="mt-3 text-lg font-semibold text-[#1C2329]">Sin brechas reportadas</div>
          <div className="text-sm text-[#6B7280] mt-1 max-w-md mx-auto">
            Cuando ocurra un incidente, reporte de inmediato aquí para arrancar el timer de 72h y generar el plan de acción legal.
          </div>
        </div>
      ) : (
        <div className="border border-[#E5E7EB] bg-white overflow-x-auto">
          <table className="pv-table" data-testid="brechas-table">
            <thead><tr>
              <th style={{ width: 100 }}>Riesgo</th>
              <th style={{ width: 130 }}>72h Agencia</th>
              <th>Título</th>
              <th style={{ width: 180 }}>Etapa actual</th>
              <th style={{ width: 140 }}>Tareas legales</th>
            </tr></thead>
            <tbody>
              {items.map((b) => (
                <BreachRow key={b.id} b={b} meta={meta}
                  onOpen={() => navigate(`/app/brechas/${b.id}`)} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating && (
        <BreachWizard meta={meta} systems={systems}
          onCancel={() => setCreating(false)}
          onCreated={(b) => { setCreating(false); navigate(`/app/brechas/${b.id}`); }} />
      )}
    </div>
    </MissionShell>
  );
}


function BreachRow({ b, meta, onOpen }) {
  const tone = RISK_TONE[b.risk_level] || RISK_TONE.medio;
  const hoursLeft = Math.ceil((new Date(b.deadline_agencia) - new Date()) / (1000 * 3600));
  const notified = !!b.notification_agencia_sent_at;
  const overdue = hoursLeft < 0 && !notified;
  const currentStage = meta.stages?.find((s) => s.key === b.current_stage);
  const legalPending = b._task_legal_pending ?? 0;
  const total = b._task_total ?? 0;
  return (
    <tr onClick={onOpen} className="cursor-pointer" data-testid={`brecha-row-${b.id}`}>
      <td><span className="text-[11px] font-medium px-1.5 py-0.5" style={{ color: tone.color, backgroundColor: tone.bg }}>{tone.label}</span></td>
      <td>
        {notified ? <span className="text-[11.5px] text-[#065F46] inline-flex items-center gap-1"><CheckCircle2 size={11} /> Notificada</span>
        : overdue ? <span className="text-[11.5px] text-[#DC2626] inline-flex items-center gap-1 font-medium"><AlertTriangle size={11} /> {Math.abs(hoursLeft)}h vencido</span>
        : <span className={`text-[11.5px] inline-flex items-center gap-1 ${hoursLeft <= 12 ? 'text-[#DC2626] font-medium' : hoursLeft <= 24 ? 'text-[#B45309]' : 'text-[#374151]'}`}><Clock size={11} /> {hoursLeft}h</span>}
      </td>
      <td>
        <div className="text-[13px] font-medium text-[#1C2329]">{b.title}</div>
        <div className="text-[11px] text-[#6B7280]">{meta.types[b.incident_type]}</div>
      </td>
      <td className="text-[12px]">
        <div className="flex items-center gap-1.5">
          <CircleDot size={11} className="text-[#DC2626]" />
          <span>{currentStage?.label || '—'}</span>
        </div>
      </td>
      <td className="text-[12px]">
        {legalPending > 0 ? (
          <span className="text-[#B91C1C] font-medium">{legalPending} pendientes</span>
        ) : (
          <span className="text-[#065F46] inline-flex items-center gap-1"><CheckCircle2 size={11} /> al día</span>
        )}
        <span className="text-[#9CA3AF] ml-1.5">/ {total}</span>
      </td>
    </tr>
  );
}


function BreachWizard({ meta, systems, onCancel, onCreated }) {
  const now = new Date().toISOString().slice(0, 16);
  const [form, setForm] = useState({
    title: '', description: '', incident_type: 'otro',
    discovered_at: now + ':00Z', risk_level: 'medio',
    affected_system_ids: [], affected_data_categories: [],
    estimated_subjects_count: '', containment_actions: '',
  });
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));
  const toggle = (k, val) => setForm((f) => ({ ...f, [k]: f[k].includes(val) ? f[k].filter((x) => x !== val) : [...f[k], val] }));
  const canSave = form.title.trim().length > 3 && form.description.trim().length > 10 && form.discovered_at;

  const save = async () => {
    try {
      const payload = { ...form, estimated_subjects_count: form.estimated_subjects_count ? Number(form.estimated_subjects_count) : null };
      const { data } = await api.post('/breaches', payload);
      toast.success('Brecha registrada · Plan de acción generado');
      onCreated(data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onCancel}>
      <div className="bg-white max-w-2xl w-full max-h-[92vh] overflow-hidden flex flex-col border-t-4 border-[#DC2626]" onClick={(e) => e.stopPropagation()} data-testid="brecha-wizard">
        <div className="px-6 py-4 border-b border-[#E5E7EB] flex items-start justify-between">
          <div>
            <div className="text-[15px] font-semibold text-[#1C2329] flex items-center gap-2">
              <AlertTriangle size={15} className="text-[#DC2626]" /> Reportar brecha de seguridad
            </div>
            <div className="text-[12px] text-[#6B7280] mt-0.5">Al guardar se inicia el timer de 72h y se genera el plan de acción con las tareas legales obligatorias.</div>
          </div>
          <button onClick={onCancel} className="p-1.5 rounded hover:bg-[#F3F4F6] text-[#6B7280]"><X size={16} /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          <F label="Título breve *"><input className="pv-input" value={form.title} onChange={set('title')} placeholder="Ej: Pendrive con datos de clientes extraviado" data-testid="brecha-title" /></F>
          <div className="grid grid-cols-2 gap-4">
            <F label="Tipo de incidente"><select className="pv-input" value={form.incident_type} onChange={set('incident_type')} data-testid="brecha-type">
              {Object.entries(meta.types).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <div className="text-[11px] text-[#6B7280] mt-1">Determina las tareas adicionales sugeridas. Si no encaja en ningún tipo, seleccione «Otro».</div>
            </F>
            <F label="Nivel de riesgo"><select className="pv-input" value={form.risk_level} onChange={set('risk_level')} data-testid="brecha-risk">
              {Object.entries(meta.risks).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select></F>
          </div>
          <F label="¿Cuándo se enteraron? *"><input type="datetime-local" className="pv-input" value={form.discovered_at.slice(0, 16)}
            onChange={(e) => set('discovered_at')(e.target.value + ':00Z')} data-testid="brecha-when" />
            <div className="text-[11px] text-[#6B7280] mt-1">Momento en que la organización tomó conocimiento. Determina el vencimiento (72h después).</div>
          </F>
          <F label="Descripción del incidente *"><textarea className="pv-input" rows={4} value={form.description} onChange={set('description')}
            placeholder="Qué pasó, dónde, quién detectó, qué se sabe hasta ahora." data-testid="brecha-description" /></F>
          <div className="grid grid-cols-2 gap-4">
            <F label="Sistemas afectados">
              {systems.length === 0 ? <div className="text-[12px] text-[#6B7280]">Sin sistemas en catastro.</div> :
                <div className="flex flex-wrap gap-1.5">{systems.map((s) => (
                  <button key={s.id} type="button" onClick={() => toggle('affected_system_ids', s.id)}
                    className={`text-[11.5px] px-2 py-1 border ${form.affected_system_ids.includes(s.id) ? 'bg-[#FEF2F2] border-[#DC2626] text-[#991B1B]' : 'border-[#E5E7EB] text-[#374151]'}`}>{s.name}</button>
                ))}</div>}
            </F>
            <F label="Datos afectados">
              <div className="flex flex-wrap gap-1.5">{Object.entries(DATA_CAT_LABELS).map(([k, v]) => (
                <button key={k} type="button" onClick={() => toggle('affected_data_categories', k)}
                  className={`text-[11.5px] px-2 py-1 border ${form.affected_data_categories.includes(k) ? 'bg-[#FEF2F2] border-[#DC2626] text-[#991B1B]' : 'border-[#E5E7EB] text-[#374151]'}`}>{v}</button>
              ))}</div>
            </F>
          </div>
          <F label="Titulares estimados afectados">
            <input type="number" className="pv-input" value={form.estimated_subjects_count} onChange={set('estimated_subjects_count')} placeholder="Ej: 1500 (déjelo vacío si aún no sabe)" />
          </F>
          <F label="Medidas de contención iniciales"><textarea className="pv-input" rows={2} value={form.containment_actions} onChange={set('containment_actions')}
            placeholder="Ej: Se revocaron accesos, se cambiaron contraseñas, se aisló el equipo." /></F>
        </div>

        <div className="px-6 py-4 border-t border-[#E5E7EB] flex justify-between bg-white">
          <button className="pv-btn-secondary" onClick={onCancel}>Cancelar</button>
          <button className="px-4 py-2 bg-[#DC2626] hover:bg-[#B91C1C] text-white text-[13px] font-medium flex items-center gap-2 disabled:opacity-50"
            disabled={!canSave} onClick={save} data-testid="brecha-save">
            <AlertTriangle size={13} /> Registrar y generar plan de acción
          </button>
        </div>
      </div>
    </div>
  );
}

function F({ label, children }) {
  return <div><label className="block text-[12px] font-medium text-[#374151] mb-1.5">{label}</label>{children}</div>;
}
